import { useState } from "react";
import { useAppModal } from "./AppModalProvider";
import { submitReport } from "apis";

interface ReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  targetType: "service" | "user" | "post";
  targetId: string;
  targetName?: string;
}

const reportReasons = [
  { id: "inappropriate", label: "부적절한 콘텐츠", desc: "선정적, 폭력적, 혐오 표현 등" },
  { id: "fraud", label: "사기 또는 허위 정보", desc: "거짓 정보, 허위 서비스, 미이행" },
  { id: "illegal", label: "불법 콘텐츠", desc: "법률에 위반되는 내용" },
  { id: "copyright", label: "저작권 침해", desc: "타인의 저작물 무단 사용" },
  { id: "spam", label: "스팸 또는 광고", desc: "반복적인 홍보성 콘텐츠" },
  { id: "other", label: "기타", desc: "위에 해당하지 않는 사유" },
];

const targetTypeLabels: Record<string, string> = {
  service: "서비스",
  user: "사용자",
  post: "게시글",
};

export default function ReportModal({ isOpen, onClose, targetType, targetId, targetName }: ReportModalProps) {
  const [selectedReason, setSelectedReason] = useState<string | null>(null);
  const [detail, setDetail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async () => {
    if (!selectedReason) return;
    setIsSubmitting(true);

    try {
      await submitReport(targetType, targetId, selectedReason, detail.trim() || undefined);
      setIsSubmitted(true);
    } catch (e) {
      console.error("Report failed", e);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setSelectedReason(null);
    setDetail("");
    setIsSubmitted(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 px-4" onClick={handleClose}>
      <div
        className="w-full max-w-[420px] bg-white rounded-2xl shadow-2xl animate-fadeIn overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-achromatic-50">
          <h3 className="text-[15px] font-bold text-achromatic-900">
            🚨 {targetTypeLabels[targetType]} 신고
          </h3>
          <button onClick={handleClose} className="text-achromatic-400 hover:text-achromatic-600 text-lg">
            ✕
          </button>
        </div>

        {isSubmitted ? (
          /* Success state */
          <div className="p-8 text-center">
            <span className="text-4xl block mb-3">✅</span>
            <h4 className="text-[15px] font-bold text-achromatic-900 mb-2">신고가 접수되었습니다</h4>
            <p className="text-[13px] text-achromatic-400 mb-5">
              검토 후 적절한 조치를 취하겠습니다. 감사합니다.
            </p>
            <button
              onClick={handleClose}
              className="px-6 py-2.5 bg-achromatic-900 text-white rounded-xl text-[13px] font-bold hover:bg-achromatic-800 transition-colors"
            >
              확인
            </button>
          </div>
        ) : (
          <>
            {/* Target info */}
            {targetName && (
              <div className="px-5 py-3 bg-achromatic-50/50">
                <p className="text-[12px] text-achromatic-400">
                  신고 대상: <span className="font-semibold text-achromatic-700">{targetName}</span>
                </p>
              </div>
            )}

            {/* Reason selection */}
            <div className="px-5 py-4 max-h-[320px] overflow-y-auto">
              <p className="text-[12px] text-achromatic-500 mb-3">신고 사유를 선택해주세요</p>
              <div className="flex flex-col gap-2">
                {reportReasons.map((reason) => (
                  <button
                    key={reason.id}
                    onClick={() => setSelectedReason(reason.id)}
                    className={`text-left px-4 py-3 rounded-xl border transition-all ${
                      selectedReason === reason.id
                        ? "border-red-300 bg-red-50"
                        : "border-achromatic-100 hover:border-achromatic-200 hover:bg-achromatic-50"
                    }`}
                  >
                    <span className={`text-[13px] font-semibold ${
                      selectedReason === reason.id ? "text-red-600" : "text-achromatic-800"
                    }`}>
                      {reason.label}
                    </span>
                    <span className="block text-[11px] text-achromatic-400 mt-0.5">{reason.desc}</span>
                  </button>
                ))}
              </div>

              {/* Detail input */}
              {selectedReason && (
                <div className="mt-4">
                  <textarea
                    value={detail}
                    onChange={(e) => setDetail(e.target.value)}
                    placeholder="추가 설명을 입력해주세요 (선택사항)"
                    className="w-full px-4 py-3 bg-achromatic-50 rounded-xl text-[13px] text-achromatic-800 placeholder:text-achromatic-300 resize-none h-[80px] focus:outline-none focus:ring-2 focus:ring-red-200 transition-all"
                    maxLength={500}
                  />
                  <p className="text-right text-[10px] text-achromatic-300 mt-1">{detail.length}/500</p>
                </div>
              )}
            </div>

            {/* Submit */}
            <div className="px-5 py-4 border-t border-achromatic-50">
              <button
                onClick={handleSubmit}
                disabled={!selectedReason || isSubmitting}
                className={`w-full py-3 rounded-xl text-[14px] font-bold transition-all ${
                  selectedReason && !isSubmitting
                    ? "bg-red-500 text-white hover:bg-red-600 active:scale-[0.98]"
                    : "bg-achromatic-100 text-achromatic-400 cursor-not-allowed"
                }`}
              >
                {isSubmitting ? "접수 중..." : "신고하기"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
